import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { MetricIdentity } from "@/metrics/catalog"
import { usePersonasList } from "@/persona"
import { useTestSetsList } from "@/test-set/catalog"
import { useOrganization } from "@clerk/nextjs"
import {
  Alert,
  Button,
  Table,
  type ColumnDef,
  type SortDescriptor,
} from "@coval/ui"

import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import { useQueryParams } from "@/hooks/useQueryParams"

import type { ChartClickData } from "../types/chart"
import { GROUP_BY_TYPE, type GroupByType } from "../types/chart-types"
import type {
  FilterType,
  MetricFilter,
  WidgetFilterConfig,
} from "../types/core"
import { getMetadataGroupDisplayName } from "../utils/chart/splitKeys"
import type { MetricValue } from "../utils/chartShared"
import { resolveDataSource } from "../utils/dataSource"
import { buildTableCellConversationsPath } from "../utils/tableCellConversationsPath"
import { buildTableCellDrillDownClickData } from "../utils/tableCellDrillDown"
import {
  buildTableColumns,
  buildTableRows,
  formatMetricCellValue,
  getTableCellSortValue,
  resolveTableExportState,
  resolveTableMetricIds,
  resolveTableMetricOutputType,
  type TableColumnDefinition,
  type TableRowData,
} from "../utils/tableWidgetData"
import {
  getDefaultTableSortColumn,
  resolveTableSortColumn,
} from "../utils/tableWidgetSorting"
import type { MetricWidgetConfig, WidgetExportData } from "./BaseWidget"
import { ChartWidgetSkeleton } from "./ChartWidgetSkeleton"
import { WidgetNoDataState } from "./WidgetNoDataState"

interface TableWidgetTableProps {
  data: {
    dateRange: { start: Date; end: Date }
    error: string | null
    metricRefreshError?: string | null
    retry?: () => void
    isGlobalOverrideActive: boolean
    isLoading: boolean
    metricRecords?: Record<string, MetricIdentity>
    metricValues: MetricValue[]
  }
  config: MetricWidgetConfig &
    WidgetFilterConfig & {
      type: "table" | "chart"
      groupBy?: GroupByType
      groupByMetadataKey?: string
      filters?: FilterType
      metricFilter?: MetricFilter[]
    }
  onExportDataReady?: (data: WidgetExportData | null) => void
  /** Opens the conversations behind one metric cell. Cells are inert without it. */
  onCellClick?: (
    clickData: ChartClickData,
    conversationsHref: string | null,
  ) => void
}

export type { TableRowData } from "../utils/tableWidgetData"

const METRIC_BACKGROUND_EXCLUDED_COLUMN_KEYS = new Set([
  "test_set_id",
  "run_agent_id",
  "persona_id",
  "run_template_name",
  "mutation_id",
  "count",
])

const MAX_METRIC_TINT_PERCENT = 40

const getMetricCellBackgroundColor = ({
  columnKey,
  value,
}: {
  columnKey: string
  value: number | undefined
}): string | undefined => {
  if (METRIC_BACKGROUND_EXCLUDED_COLUMN_KEYS.has(columnKey) || value == null) {
    return undefined
  }

  // The strongest tint stays at 40% of the status surface so the table's text
  // keeps at least 6:1 contrast on it in light and dark themes.
  const intensity = Math.round(
    Math.abs(value - 0.5) * 2 * MAX_METRIC_TINT_PERCENT,
  )
  const surface =
    value >= 0.5 ? "--coval-success-surface" : "--coval-danger-surface"

  return `color-mix(in srgb, var(${surface}) ${intensity}%, transparent)`
}

const TableWidgetTableContent: React.FC<TableWidgetTableProps> = ({
  data,
  config,
  onExportDataReady,
  onCellClick,
}) => {
  const { organization } = useOrganization()
  const { getHref } = useQueryParams()

  const metricIds = useMemo(
    () =>
      resolveTableMetricIds({
        filters: config.filters,
        metricId: config.metricId,
        metricIds: config.metricIds,
      }),
    [config.filters, config.metricId, config.metricIds],
  )
  const showPerMetricDenominators = metricIds.length > 1
  const defaultSortColumn = getDefaultTableSortColumn({
    groupBy: config.groupBy,
    groupByMetadataKey: config.groupByMetadataKey,
    metricIds,
  })

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    columnId: defaultSortColumn,
    direction: "asc",
  })

  const selectedTestSetIds = useMemo(
    () => config.filters?.testSetIds || [],
    [config.filters?.testSetIds],
  )

  const { data: testSetList } = useTestSetsList({
    enabled: selectedTestSetIds.length > 0,
  })

  const { data: personaList } = usePersonasList({
    enabled: config.groupBy === GROUP_BY_TYPE.PERSONA,
  })

  const testSetNameMap = useMemo(() => {
    const nameMap = new Map<string, string>()

    if (testSetList) {
      testSetList.forEach((testSet) => {
        nameMap.set(testSet.id, testSet.display_name)
      })
    }

    return nameMap
  }, [testSetList])

  const personaNameMap = useMemo(() => {
    const nameMap = new Map<string, string>()

    if (personaList) {
      personaList.forEach((persona: { id: string; display_name: string }) => {
        nameMap.set(persona.id, persona.display_name)
      })
    }

    return nameMap
  }, [personaList])

  const tableRows = useMemo(
    () =>
      buildTableRows({
        groupBy: config.groupBy,
        groupByMetadataKey: config.groupByMetadataKey,
        metricIds,
        metricValues: data.metricValues,
        personaNameMap,
        testSetNameMap,
      }),
    [
      config.groupBy,
      config.groupByMetadataKey,
      data.metricValues,
      metricIds,
      personaNameMap,
      testSetNameMap,
    ],
  )

  const columns = useMemo<TableColumnDefinition[]>(
    () =>
      buildTableColumns({
        groupBy: config.groupBy,
        groupByMetadataKey: config.groupByMetadataKey,
        metricIds,
        metricRecords: data.metricRecords ?? null,
      }),
    [config.groupBy, config.groupByMetadataKey, data.metricRecords, metricIds],
  )

  const activeSortDescriptor = useMemo<SortDescriptor>(() => {
    const columnId = resolveTableSortColumn({
      columns,
      fallbackColumn: defaultSortColumn,
      requestedColumn: sortDescriptor.columnId,
    })

    return columnId === sortDescriptor.columnId
      ? sortDescriptor
      : { columnId, direction: "asc" }
  }, [columns, defaultSortColumn, sortDescriptor])

  // A header toggles between ascending and descending; the third, unsorted
  // step of the Table's cycle returns to ascending on the same column.
  const handleSortChange = useCallback(
    (descriptor: SortDescriptor | null) => {
      setSortDescriptor(
        descriptor ?? {
          columnId: activeSortDescriptor.columnId,
          direction: "asc",
        },
      )
    },
    [activeSortDescriptor.columnId],
  )

  const sortedRows = useMemo(() => {
    return [...tableRows].sort((a, b) => {
      const columnKey = activeSortDescriptor.columnId
      const aValue = getTableCellSortValue(a, columnKey)
      const bValue = getTableCellSortValue(b, columnKey)

      if (aValue == null && bValue == null) {
        return 0
      }
      if (aValue == null) {
        return 1
      }
      if (bValue == null) {
        return -1
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return activeSortDescriptor.direction === "asc"
          ? aValue - bValue
          : bValue - aValue
      }

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        },
      )

      return activeSortDescriptor.direction === "asc" ? comparison : -comparison
    })
  }, [activeSortDescriptor, tableRows])

  const getRowEntityHref = useCallback(
    (row: TableRowData): string | null => {
      if (
        !organization?.slug ||
        !config.groupBy ||
        config.groupBy === GROUP_BY_TYPE.TEMPLATE ||
        config.groupBy === GROUP_BY_TYPE.MUTATION
      ) {
        return null
      }

      const groupByToField: Record<string, string> = {
        test_set: "test_set_id",
        agent: "run_agent_id",
        persona: "persona_id",
      }

      const groupByToUrl: Record<string, string> = {
        test_set: "test-sets/:id",
        agent: "agents/:id",
        persona: "personas?id=:id",
      }

      const urlKey = groupByToUrl[config.groupBy as keyof typeof groupByToUrl]
      const fieldKey =
        groupByToField[config.groupBy as keyof typeof groupByToField]

      if (!urlKey || !fieldKey) {
        return null
      }

      const rawValue = (row as unknown as Record<string, unknown>)[fieldKey]
      if (typeof rawValue !== "string" || rawValue.length === 0) {
        return null
      }

      const entityPath = `/${urlKey.replace(":id", rawValue)}`
      return getHref(entityPath, organization.slug)
    },
    [config.groupBy, getHref, organization?.slug],
  )

  const handleRowClick = useCallback(
    (row: TableRowData): void => {
      const href = getRowEntityHref(row)
      if (href) {
        window.open(`${window.location.origin}${href}`, "_blank")
      }
    },
    [getRowEntityHref],
  )

  const getCellConversationsHref = useCallback(
    (column: TableColumnDefinition, row: TableRowData) => {
      if (!metricIds.includes(column.key)) {
        return null
      }

      const path = buildTableCellConversationsPath({
        dataSource: resolveDataSource(config),
        dateRange: data.dateRange,
        filters: config.filters,
        groupBy: config.groupBy,
        groupByMetadataKey: config.groupByMetadataKey,
        metricFilter: config.metricFilter,
        metricId: column.key,
        metricLabel: column.label,
        metricOutputType: resolveTableMetricOutputType(
          data.metricRecords?.[column.key],
        ),
        row,
      })

      return path ? getHref(path, organization?.slug ?? undefined) : null
    },
    [
      config,
      data.dateRange,
      data.metricRecords,
      getHref,
      metricIds,
      organization?.slug,
    ],
  )

  const getCellClickData = useCallback(
    (column: TableColumnDefinition, row: TableRowData) => {
      if (!onCellClick || !metricIds.includes(column.key)) {
        return null
      }

      return buildTableCellDrillDownClickData({
        aggregation: config.aggregation,
        dataSource: resolveDataSource(config),
        dateRange: data.dateRange,
        filters: config.filters,
        groupBy: config.groupBy,
        groupByMetadataKey: config.groupByMetadataKey,
        metricFilter: config.metricFilter,
        metricId: column.key,
        metricLabel: column.label,
        metricOutputType: resolveTableMetricOutputType(
          data.metricRecords?.[column.key],
        ),
        row,
      })
    },
    [config, data.dateRange, data.metricRecords, metricIds, onCellClick],
  )

  const renderCell = useCallback(
    (column: { key: string }, row: TableRowData) => {
      const dimensionRenderers: Record<
        string,
        (tableRow: TableRowData) => string
      > = {
        test_set_id: (tableRow) =>
          String(tableRow.testSetName || tableRow.test_set_id || "-"),
        run_agent_id: (tableRow) =>
          String(tableRow.agentName || tableRow.run_agent_id || "-"),
        mutation_id: (tableRow) =>
          String(tableRow.mutationName || tableRow.mutation_id || "-"),
        persona_id: (tableRow) =>
          String(tableRow.personaName || tableRow.persona_id || "-"),
        run_template_name: (tableRow) =>
          String(tableRow.templateName || tableRow.run_template_name || "-"),
        assignee: (tableRow) => String(tableRow.assignee || "Unassigned"),
        metadataValue: (tableRow) =>
          tableRow.metadataValue
            ? getMetadataGroupDisplayName(tableRow.metadataValue)
            : "-",
      }

      const dimensionRenderer = dimensionRenderers[column.key]
      if (dimensionRenderer) {
        return dimensionRenderer(row)
      }

      if (column.key === "count") {
        return String(row.count ?? 0)
      }

      const formattedMetricValue = formatMetricCellValue({
        cell: row.metricCells[column.key],
        showDenominator: showPerMetricDenominators,
      })

      if (formattedMetricValue == null) {
        return <span className="text-coval-text-muted">-</span>
      }

      return <span className="text-center">{formattedMetricValue}</span>
    },
    [showPerMetricDenominators],
  )

  const isRowNavigable = Boolean(
    config.groupBy &&
    config.groupBy !== GROUP_BY_TYPE.ASSIGNEE &&
    config.groupBy !== GROUP_BY_TYPE.TEMPLATE &&
    config.groupBy !== GROUP_BY_TYPE.MUTATION,
  )

  const getRowId = useCallback(
    (row: TableRowData) =>
      String(
        row.test_set_id ||
          row.run_agent_id ||
          row.persona_id ||
          row.run_template_name ||
          row.mutation_id ||
          row.assignee ||
          row.metadataValue ||
          "aggregated",
      ),
    [],
  )

  const { activeItemId, handlePointerEnter, handlePointerLeave } =
    useCollectionPageShortcuts({
      enabled: isRowNavigable,
      getItemId: getRowId,
      itemLabel: "row",
      items: isRowNavigable ? sortedRows : [],
      onOpenItem: handleRowClick,
    })

  const isRowClickable = useCallback(
    (row: TableRowData) => getRowEntityHref(row) !== null,
    [getRowEntityHref],
  )

  const renderTableCell = useCallback(
    (column: TableColumnDefinition, row: TableRowData) => {
      const content = renderCell(column, row)
      const backgroundColor = getMetricCellBackgroundColor({
        columnKey: column.key,
        value: row.metricCells[column.key]?.value,
      })
      const body = backgroundColor ? (
        <span
          className="whitespace-nowrap rounded-sm px-1.5 py-0.5"
          style={{ backgroundColor }}
        >
          {content}
        </span>
      ) : (
        <span className="whitespace-nowrap">{content}</span>
      )
      const cellClickData = getCellClickData(column, row)
      if (!cellClickData || !onCellClick) {
        return body
      }
      return (
        <button
          type="button"
          className="cursor-pointer hover:underline focus-visible:underline"
          data-testid="dashboard-table-widget-metric-cell-drilldown"
          onClick={(event) => {
            event.stopPropagation()
            onCellClick(cellClickData, getCellConversationsHref(column, row))
          }}
        >
          {body}
        </button>
      )
    },
    [getCellClickData, getCellConversationsHref, onCellClick, renderCell],
  )

  const tableColumns = useMemo<ColumnDef<TableRowData>[]>(
    () =>
      columns.map((column) => ({
        id: column.key,
        header: (
          <span className="capitalize">{column.label.replace("_", " ")}</span>
        ),
        headerLabel: column.label.replace("_", " "),
        sortable: column.sortable,
        cell: (row) => renderTableCell(column, row),
      })),
    [columns, renderTableCell],
  )

  const rowAttributes = useCallback(
    (row: TableRowData) => ({ "data-widget-row-id": getRowId(row) }),
    [getRowId],
  )

  const prevExportDataRef = useRef("")
  useEffect(() => {
    if (!onExportDataReady || data.isLoading) {
      return
    }

    const { exportDataKey, tableData } = resolveTableExportState({
      columns,
      dateRange: data.dateRange,
      error: data.error,
      rows: sortedRows,
    })
    if (prevExportDataRef.current === exportDataKey) {
      return
    }

    prevExportDataRef.current = exportDataKey

    if (tableData) {
      onExportDataReady({
        tableData,
        dateRange: data.dateRange,
        widgetType: "table",
      })
      return
    }

    onExportDataReady(null)
  }, [
    columns,
    data.dateRange,
    data.error,
    data.isLoading,
    onExportDataReady,
    sortedRows,
  ])

  if (data.error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-coval-text-muted">
          <div className="text-sm">{data.error}</div>
          {data.retry && (
            <Button variant="secondary" className="mt-2" onClick={data.retry}>
              Retry
            </Button>
          )}
        </div>
      </div>
    )
  }

  if (data.isLoading) {
    return <ChartWidgetSkeleton />
  }

  if (
    data.metricValues.length === 0 &&
    Object.keys(data.metricRecords ?? {}).length === 0
  ) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-coval-text-muted">
          <div className="text-sm">This widget's metrics can't be found</div>
          <div className="text-xs mt-1">
            Edit the widget to pick its metrics again
          </div>
        </div>
      </div>
    )
  }

  if (data.metricValues.length === 0) {
    return <WidgetNoDataState dateRange={data.dateRange} />
  }

  if (tableRows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-sm text-coval-text-muted">
          No rows to show for this grouping
        </div>
      </div>
    )
  }

  return (
    <Table<TableRowData>
      aria-label="Table"
      data={sortedRows}
      columns={tableColumns}
      getRowId={getRowId}
      defaultSort={activeSortDescriptor}
      onSortChange={handleSortChange}
      sortStorageKey={null}
      onRowClick={isRowNavigable ? handleRowClick : undefined}
      isRowClickable={isRowClickable}
      rowClickDescription="Press Enter to open in a new tab"
      activeRowId={activeItemId}
      onRowPointerEnter={handlePointerEnter}
      onRowPointerLeave={handlePointerLeave}
      rowAttributes={rowAttributes}
      emptyState="No data available"
      edgeToEdge
      className="h-full w-full"
    />
  )
}

export function TableWidgetTable(props: TableWidgetTableProps) {
  return (
    <div className="flex h-full w-full flex-col">
      {!props.data.error && props.data.metricRefreshError && (
        <Alert variant="warning">
          Metric refresh failed. Showing the last loaded definitions.{" "}
          {props.data.retry && (
            <Button variant="secondary" onClick={props.data.retry}>
              Retry
            </Button>
          )}
        </Alert>
      )}
      <div className="min-h-0 flex-1">
        <TableWidgetTableContent {...props} />
      </div>
    </div>
  )
}
